import { ArrowDown, ArrowUp, Copy, Plus, Replace, Settings2, Trash2 } from 'lucide-react'
import { PickerMenuItem, PickerMenuSeparator } from '@/components/picker-menu'
import { ManufacturerAssignmentPicker } from '@/components/manufacturer-assignment-picker'
import { WarehouseSourcePicker } from '@/components/warehouse-source-picker'
import type { WarehouseSourceChoice, WarehouseStockOption } from '@/api/warehouse-sourcing'
import type { QuoteLine } from '../api/types'
import { ManufacturerLink } from '../catalog/links'
import { EquipmentPlanMenu } from './protection-plans'
import type { EquipmentProtection, PlanActions } from './protection-plans-api'

/**
 * What a worksheet row can ask the Equipment stage to do beyond pricing (USA-1140): the controls
 * the separate Equipment list had. The stage turns each into a queued quote edit.
 */
export type WorksheetLineActions = PlanActions & {
  onConfigure: (line: QuoteLine) => void
  onAlternatives: (line: QuoteLine, trigger: HTMLButtonElement) => void
  onReplace: (line: QuoteLine) => void
  onDuplicate: (line: QuoteLine) => void
  onRemove: (line: QuoteLine) => void
  onAddCustomChild: (line: QuoteLine) => void
  onMove: (line: QuoteLine, position: number) => void
  /** A top-level custom item is ordered from the manufacturer Sales chooses (USA-1100). */
  onManufacturer: (line: QuoteLine, manufacturerId: number) => void
  /** What our warehouse holds of the quote's products, per condition (USA-1574). */
  warehouseStock?: ReadonlyMap<number, readonly WarehouseStockOption[]>
  /** A catalog line pulled from our warehouse in a condition, or back from its manufacturer. */
  onWarehouseSource: (line: QuoteLine, source: WarehouseSourceChoice | null, label: string | null) => void
}

/** Where a top-level catalog line is ordered from, when our warehouse holds its product (USA-1574). */
export function LineSource({ line, actions }: { line: QuoteLine; actions?: WorksheetLineActions }) {
  if (line.line_type !== 'product' || line.parent_line_id || line.product_id == null) return null
  return (
    <WarehouseSourcePicker
      value={line.warehouse_source}
      options={actions?.warehouseStock?.get(line.product_id)}
      lineLabel={line.model ?? line.description}
      readOnly={!actions}
      onChange={(source, label) => actions?.onWarehouseSource(line, source, label)}
    />
  )
}

/** The manufacturer under a line's name: a picker for custom items, else a link. */
export function LineManufacturer({ line, actions }: { line: QuoteLine; actions?: WorksheetLineActions }) {
  const pickable = actions && line.line_type === 'custom' && (!line.parent_line_id || line.is_warranty)
  if (pickable) {
    return (
      <ManufacturerAssignmentPicker
        allowCreate
        className="min-h-6 px-1 py-0 text-[11px] font-medium"
        currentId={line.manufacturer_id}
        currentName={line.manufacturer}
        onChange={(manufacturer) => actions.onManufacturer(line, manufacturer.id)}
      />
    )
  }
  if (!line.manufacturer) return null
  return (
    <ManufacturerLink className="text-[11px] text-muted-foreground" uuid={line.manufacturer_uuid}>
      {line.manufacturer}
    </ManufacturerLink>
  )
}

/** The line's configuration and alternatives links; its chosen options are sub-rows beneath it. */
export function LineEquipmentControls({
  line,
  actions,
  protection,
  plans = [],
}: {
  line: QuoteLine
  actions: WorksheetLineActions
  /** The line's Safeware plans at its Sell (USA-1162). */
  protection?: EquipmentProtection
  plans?: QuoteLine[]
}) {
  if (!line.product_id || line.protection_plan) return null
  const name = line.model || line.description
  return (
    <>
      <div className="mt-1 flex flex-wrap items-center gap-x-3">
        <button
          className="inline-flex min-h-7 items-center gap-1 rounded-sm text-[11px] font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          onClick={() => actions.onConfigure(line)}
          type="button"
        >
          <Settings2 aria-hidden="true" className="size-3.5" />
          Options & accessories
        </button>
        {line.line_type === 'product' && !line.parent_line_id ? (
          <button
            aria-haspopup="dialog"
            aria-label={`Alternatives for ${name}`}
            className="inline-flex min-h-7 items-center gap-1 rounded-sm text-[11px] font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            onClick={(event) => actions.onAlternatives(line, event.currentTarget)}
            type="button"
          >
            Alternatives
            {line.alternative_product_ids?.length ? (
              <span className="rounded-full bg-muted px-1.5 tabular-nums">
                {line.alternative_product_ids.length} on draft
              </span>
            ) : null}
          </button>
        ) : null}
        <EquipmentPlanMenu actions={actions} line={line} plans={plans} protection={protection} />
      </div>
    </>
  )
}

/** The Equipment list's line actions, as items in the worksheet row's menu. */
export function LineActionItems({
  line,
  moves,
  actions,
  onPick,
}: {
  line: QuoteLine
  /** Where Move up / Move down take the line in the view it is listed in (USA-1344). */
  moves: { up?: number; down?: number }
  actions: WorksheetLineActions
  /** Closes the menu once an action is picked. */
  onPick: () => void
}) {
  const { up, down } = moves
  const pick = (action: () => void) => () => {
    onPick()
    action()
  }
  return (
    <>
      <PickerMenuItem disabled={up === undefined} onSelect={pick(() => up !== undefined && actions.onMove(line, up))}>
        <ArrowUp />
        Move up
      </PickerMenuItem>
      <PickerMenuItem
        disabled={down === undefined}
        onSelect={pick(() => down !== undefined && actions.onMove(line, down))}
      >
        <ArrowDown />
        Move down
      </PickerMenuItem>
      <PickerMenuItem onSelect={pick(() => actions.onDuplicate(line))}>
        <Copy />
        Duplicate item
      </PickerMenuItem>
      <PickerMenuItem onSelect={pick(() => actions.onAddCustomChild(line))}>
        <Plus />
        Add custom sub-item
      </PickerMenuItem>
      {/* A custom item can be swapped for the catalog product found for it (USA-1338). */}
      {line.product_id || (line.line_type === 'custom' && !line.is_warranty) ? (
        <PickerMenuItem onSelect={pick(() => actions.onReplace(line))}>
          <Replace />
          Replace
        </PickerMenuItem>
      ) : null}
      <PickerMenuSeparator />
      <PickerMenuItem onSelect={pick(() => actions.onRemove(line))} variant="destructive">
        <Trash2 />
        Remove item
      </PickerMenuItem>
    </>
  )
}
